<script setup lang="ts">
const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const { createPublicSession } = useApi()
const { trackWorkPlay } = useFunnelEvent()

definePageMeta({
  layout: false,
})

// Extract slug from route
const slug = computed(() => {
  const parts = route.path.split('/').filter(Boolean)
  // /works/<slug>
  return parts[parts.length - 1]?.toLowerCase() ?? ''
})

// SSR: fetch work metadata for OGP
const { data: work } = await useAsyncData(
  `work-${slug.value}`,
  () => $fetch<Work>(
    `${runtimeConfig.public.apiBase}/api/v1/public/works/${slug.value}`,
  ),
)

const defaultDescription = '夢中になれたら、学びも遊び。楽しむことから始める、紙とウェブのプログラミング教材。'
const defaultOgImage = `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`

// #707: X(Twitter) OGP は静止画 PNG (ogpImageUrl) を最優先で使う。X はアニメ GIF を
// カードでは先頭フレームしか出さないため、意図した1枚を出すには専用の静止画が要る。
// ogpImageUrl 未生成の旧 Work はカバー GIF にフォールバック。
//
// #773: 静止画は **サーバ側で 1200×630 のカードに合成済み**（`services/ogp_card.py`）。
// 以前は「撮った PNG は正方形」という前提で twitterCard を 'summary' にしていたが、
// 実際の静止画は `pyxel.screenshot(scale=2)` そのままで縦横比が作品ごとに違い
// (prod 実測で 0.77〜1.78)、正方形カードでは横長の作品が大きく欠けていた。
// 固定キャンバスに載せたので 'summary_large_image' に固定できる。
const ogImageUrl = computed(() => work.value?.ogpImageUrl || work.value?.coverImageUrl || defaultOgImage)

useSeoMeta({
  title: () => work.value ? `${work.value.title} | Code & Magic` : 'Code & Magic',
  description: () => work.value?.description || defaultDescription,
  ogTitle: () => work.value ? `${work.value.title} | Code & Magic` : 'Code & Magic',
  ogDescription: () => work.value?.description || defaultDescription,
  ogImage: () => ogImageUrl.value,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  twitterImage: () => ogImageUrl.value,
  twitterCard: 'summary_large_image',
})

// JSON-LD: CreativeWork (動的 — 作品データに基づく)
useJsonLd(computed(() => {
  if (!work.value) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    'name': work.value.title,
    'description': work.value.description,
    'author': { '@type': 'Person', 'name': work.value.author },
    'url': `${runtimeConfig.public.serviceDomain}/works/${slug.value}`,
    'image': work.value.ogpImageUrl || work.value.coverImageUrl || defaultOgImage,
    'keywords': work.value.keywords?.join(', '),
    'datePublished': work.value.publishedAt,
    'interactionStatistic': [
      {
        '@type': 'InteractionCounter',
        'interactionType': 'https://schema.org/PlayAction',
        'userInteractionCount': work.value.plays,
      },
      {
        '@type': 'InteractionCounter',
        'interactionType': 'https://schema.org/LikeAction',
        'userInteractionCount': work.value.likes,
      },
    ],
    'isPartOf': {
      '@type': 'WebApplication',
      'name': 'Code & Magic',
      'url': runtimeConfig.public.serviceDomain,
    },
  }
}))

const sessionUrl = ref<string>('')
// #663: Pyodide built-in packages preloaded for this work (e.g. ["numpy"]).
const sessionPackages = ref<string[]>([])
// #450: cloud-save scope (for unauthenticated localStorage namespacing).
const sessionSaveScope = ref<string | null>(null)

onMounted(async () => {
  if (!slug.value) {
    return showError(createError({ statusCode: 404, statusMessage: '作品が見つかりません。', fatal: true }))
  }

  // #844: セッション作成の失敗を握らないと **unhandled rejection のまま真っ白な
  // 没入画面**になる（コンソールにだけ FetchError が出る）。起きる条件は
  // 「Work はあるが pyxapp artifact が引けない」（`create_session_handler` の
  // slug ブランチが 404 を返す）と、API 障害の 2 つ。
  //
  // ⚠ 前者は **prod では 0 件**（公開 53 件すべて artifact あり・2026-08-31 実測）だが、
  // ローカルの seed には「カード表示専用で pyxapp を持たない Work」が 5 件あり
  // （`seed/works/items.local/` の doctor-d 3 件と long_text 2 件）、#843 の公開
  // ギャラリーがそれらを並べるようになって踏めるようになった。
  try {
    const session = await createPublicSession(slug.value)
    trackWorkPlay(slug.value, false)
    sessionUrl.value = session.sessionUrl
    sessionPackages.value = session.pyodidePackages ?? []
    sessionSaveScope.value = session.saveScope ?? null
  }
  catch (e) {
    const status = (e as { statusCode?: number, status?: number })?.statusCode
      ?? (e as { status?: number })?.status
    if (status === 404) {
      showError(createError({
        statusCode: 404,
        statusMessage: 'この作品はいま遊べません。',
        fatal: true,
      }))
    }
    else {
      showError(createError({
        statusCode: 500,
        statusMessage: '作品の読み込みに失敗しました。時間をおいて試してください。',
        fatal: true,
      }))
    }
  }
})
</script>

<template>
  <NuxtLayout name="immersive">
    <ClientOnly>
      <WorkPlay
        v-if="sessionUrl"
        :session-url="sessionUrl"
        :share-slug="slug"
        :share-title="work?.title"
        :author="work?.author"
        :gamepad-compatible="work?.gamepadCompatible ?? false"
        :work="work"
        :pyodide-packages="sessionPackages"
        :save-scope="sessionSaveScope"
      />
    </ClientOnly>
  </NuxtLayout>
</template>
